import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { offers, faqsOffres, globalCTA } from '../content';
import HeroSection from '../components/HeroSection';
import Seo from '../components/Seo';

const Offres = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  const mainOffers = offers.filter(o => !o.isBanner);
  const bannerOffer = offers.find(o => o.isBanner);

  const comparisonRows = [
    { label: 'Page de réservation personnalisée', ess: true, pro: true, prem: true },
    { label: 'Rappels email + WhatsApp', ess: true, pro: true, prem: true },
    { label: 'Rappels SMS', ess: '0,09 €/SMS', pro: '0,09 €/SMS', prem: '0,09 €/SMS' },
    { label: 'Intégrations Zoom / Google Meet / Microsoft Teams', ess: true, pro: true, prem: true },
    { label: 'Multilangue', ess: true, pro: true, prem: true },
    { label: 'Évaluations et avis clients', ess: true, pro: true, prem: true },
    { label: 'Avoir l\'air occupé', ess: true, pro: true, prem: true },
    { label: 'Bons de réduction', ess: true, pro: true, prem: true },
    { label: 'Espace client', ess: true, pro: true, prem: true },
    { label: 'Facturation intégrée', ess: true, pro: true, prem: true },
    { label: 'Calendrier + sync (Google, Outlook, Apple)', ess: true, pro: true, prem: true },
    { label: 'Paiements & acomptes en ligne', ess: false, pro: true, prem: true },
    { label: 'Fiche client personnalisée', ess: false, pro: true, prem: true },
    { label: 'Notes internes', ess: false, pro: true, prem: true },
    { label: 'RDV récurrents', ess: false, pro: true, prem: true },
    { label: 'Réservations de groupe', ess: false, pro: true, prem: true },
    { label: 'Google Analytics / Tag Manager', ess: false, pro: true, prem: true },
    { label: 'Sous-domaine personnalisé', ess: false, pro: true, prem: true },
    { label: 'Gestion de ressources', ess: false, pro: false, prem: true },
    { label: 'Bundles', ess: false, pro: false, prem: true },
    { label: 'Plusieurs emplacements', ess: false, pro: false, prem: true },
    { label: 'API / Zapier / Webhooks', ess: false, pro: false, prem: true },
    { label: 'Support humain (email, WhatsApp, téléphone)', ess: true, pro: true, prem: true },
    { label: 'Accompagnement à l\'installation', ess: true, pro: true, prem: true, info: true }
  ];
  const planKeys = ['ess', 'pro', 'prem'];

  const faqPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqsOffres.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer }
    }))
  };

  const productsJsonLd = mainOffers
    .filter((o) => o.priceNumeric)
    .map((o) => ({
      '@type': 'Product',
      name: `Espace Agenda — Offre ${o.name}`,
      description: o.description,
      offers: {
        '@type': 'Offer',
        price: o.priceNumeric,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        url: (typeof window !== 'undefined' ? window.location.origin : '') + '/offres'
      }
    }));

  const offresJsonLd = { '@context': 'https://schema.org', '@graph': [faqPageJsonLd, ...productsJsonLd] };


  return (
    <div className="min-h-screen">
      <Seo
        title="Tarifs et offres dès 29€/mois TTC | Espace Agenda"
        description="Découvrez nos offres dès 29€/mois TTC : installation, personnalisation, rappels et support humain inclus. Sans engagement. Devis gratuit en 20 minutes."
        jsonLd={offresJsonLd}
      />
      <HeroSection
        title="Des offres simples, adaptées à"
        titleHighlight="votre pratique"
        description="Installation guidée, accompagnement humain, support réactif. Garantie 30 jours satisfait ou remboursé."
        backgroundImage="https://images.unsplash.com/photo-1683056242525-9e0e9883a42e?crop=entropy&cs=srgb&fm=jpg&q=85"
        ctaText={globalCTA.primary}
        ctaLink="/contact"
        showCta={true}
        secondaryCtaText="Voir la solution"
        secondaryCtaLink="/solution"
      />

      {/* ================================================================
          OFFRES PRINCIPALES
      ================================================================ */}
      <section className="py-20" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-12">
            <span className="label-tag mb-4 block" style={{ color: '#7D6235' }}>Nos formules</span>
            <h2 className="font-heading font-medium text-4xl sm:text-5xl mb-4" style={{ color: '#2C352D' }}>
              Choisissez votre formule
            </h2>
            <p className="text-lg" style={{ color: '#5E6C60' }}>
              Installation, personnalisation et support humain inclus dans toutes les offres.
            </p>
          </div>

          {/* Toggle mensuel / annuel */}
          <div className="flex flex-col items-center gap-3 mb-14">
            <div className="flex items-center gap-4">
              <span
                className="text-sm font-medium"
                style={{ color: isAnnual ? '#5E6C60' : '#2C352D' }}
              >
                Mensuel
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none"
                style={{ backgroundColor: isAnnual ? '#5A7161' : '#E2DFD8' }}
                role="switch"
                aria-checked={isAnnual}
                aria-label="Basculer entre facturation mensuelle et annuelle"
                data-testid="offres-billing-toggle"
              >
                <span
                  className="inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-300"
                  style={{ transform: isAnnual ? 'translateX(24px)' : 'translateX(4px)' }}
                />
              </button>
              <span
                className="text-sm font-medium"
                style={{ color: isAnnual ? '#2C352D' : '#5E6C60' }}
              >
                Annuel
              </span>
            </div>
            <div className="h-6 flex items-center justify-center">
              {isAnnual && (
                <span
                  className="text-xs font-semibold px-3 py-1 rounded-full"
                  style={{ backgroundColor: 'rgba(158,126,74,0.12)', color: '#7D6235' }}
                  data-testid="offres-annual-badge"
                >
                  2 mois offerts
                </span>
              )}
            </div>
          </div>

          {/* Grille des 3 offres */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mainOffers.map((offer) => (
              <div
                key={offer.id}
                className={`relative rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 flex flex-col ${
                  offer.highlight ? 'scale-105' : ''
                }`}
                style={{
                  backgroundColor: offer.highlight ? '#5A7161' : '#FFFFFF',
                  border: offer.highlight ? 'none' : '1px solid #E2DFD8',
                  boxShadow: offer.highlight
                    ? '0 16px 40px rgba(90,113,97,0.25)'
                    : '0 2px 8px rgba(90,113,97,0.06)'
                }}
                data-testid={`offer-card-${offer.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {/* Badge "Le plus choisi" */}
                {offer.highlight && offer.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span
                      className="text-xs font-semibold px-4 py-1 rounded-full"
                      style={{ backgroundColor: '#7D6235', color: '#FFFFFF' }}
                    >
                      {offer.badge}
                    </span>
                  </div>
                )}

                {/* Nom + description */}
                <h3
                  className="font-heading font-medium text-2xl mb-1"
                  style={{ color: offer.highlight ? '#F9F6F0' : '#2C352D' }}
                >
                  {offer.name}
                </h3>
                <p
                  className="text-sm mb-5 leading-relaxed"
                  style={{ color: offer.highlight ? 'rgba(249,246,240,0.75)' : '#5E6C60' }}
                >
                  {offer.description}
                </p>

                {/* Bloc prix */}
                <div className="mb-2 min-h-[72px]">
                  {isAnnual && offer.priceAnnual ? (
                    <div
                      className="text-3xl font-bold font-heading"
                      style={{ color: offer.highlight ? '#F9F6F0' : '#5A7161' }}
                      data-testid={`offer-price-${offer.id}`}
                    >
                      {offer.priceAnnual}
                    </div>
                  ) : (
                    <div
                      className="text-3xl font-bold font-heading"
                      style={{ color: offer.highlight ? '#F9F6F0' : '#5A7161' }}
                      data-testid={`offer-price-${offer.id}`}
                    >
                      {offer.price}
                    </div>
                  )}
                  {offer.installation && (
                    <p
                      className="mt-1 text-xs"
                      style={{ color: offer.highlight ? 'rgba(249,246,240,0.6)' : '#5E6C60' }}
                    >
                      + {offer.installation} (installation, une fois)
                    </p>
                  )}
                  {offer.sms && (
                    <p
                      className="mt-1 text-xs font-medium"
                      style={{ color: offer.highlight ? 'rgba(249,246,240,0.8)' : '#5A7161' }}
                    >
                      {offer.sms}
                    </p>
                  )}
                </div>

                {/* Liste des features */}
                <ul className="space-y-2.5 mb-8 mt-4 flex-1">
                  {offer.features.slice(0, 8).map((feature, index) => (
                    <li key={index} className="flex items-start gap-2.5">
                      <span
                        className="flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center mt-0.5"
                        style={{
                          backgroundColor: offer.highlight
                            ? 'rgba(249,246,240,0.2)'
                            : 'rgba(90,113,97,0.12)',
                          color: offer.highlight ? '#F9F6F0' : '#5A7161'
                        }}
                      >
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span
                        className="text-sm"
                        style={{ color: offer.highlight ? 'rgba(249,246,240,0.9)' : '#5E6C60' }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Accompagnement à l'installation */}
                {offer.installSetup && (
                  <div
                    className="mb-6 pt-5"
                    style={{ borderTop: offer.highlight ? '1px solid rgba(249,246,240,0.2)' : '1px solid #E2DFD8' }}
                  >
                    <p
                      className="text-xs font-semibold uppercase tracking-wide mb-3"
                      style={{ color: offer.highlight ? 'rgba(249,246,240,0.8)' : '#7D6235' }}
                    >
                      Accompagnement à l'installation
                    </p>
                    <ul className="space-y-1.5">
                      {offer.installSetup.map((item, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-sm"
                          style={{ color: offer.highlight ? 'rgba(249,246,240,0.85)' : '#5E6C60' }}
                        >
                          <span
                            className="h-1.5 w-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: offer.highlight ? '#F9F6F0' : '#5A7161' }}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* CTA */}
                <Link to="/contact">
                  <button
                    className="w-full py-3 rounded-full text-sm font-semibold transition-all duration-200"
                    style={{
                      backgroundColor: offer.highlight ? '#F9F6F0' : '#5A7161',
                      color: offer.highlight ? '#2C352D' : '#FFFFFF'
                    }}
                    data-testid={`offer-cta-${offer.id}`}
                  >
                    {globalCTA.primary} →
                  </button>
                </Link>
              </div>
            ))}
          </div>

          {/* Bandeau Sur mesure */}
          {bannerOffer && (
            <div
              className="mt-12 rounded-3xl p-8"
              style={{ backgroundColor: '#F4F0E8', border: '1px solid #E2DFD8' }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                <div>
                  <span className="label-tag mb-2 block" style={{ color: '#7D6235' }}>Sur mesure</span>
                  <h3 className="font-heading font-medium text-2xl mb-2" style={{ color: '#2C352D' }}>
                    {bannerOffer.name}
                  </h3>
                  <p className="text-sm mb-4" style={{ color: '#5E6C60' }}>{bannerOffer.description}</p>
                  <p className="font-heading font-medium text-2xl" style={{ color: '#5A7161' }}>
                    {bannerOffer.price}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {bannerOffer.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div
                          className="flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center mt-0.5"
                          style={{ backgroundColor: 'rgba(90,113,97,0.12)', color: '#5A7161' }}
                        >
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-sm" style={{ color: '#5E6C60' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6">
                    <Link to="/contact?subject=sur-mesure">
                      <button
                        className="py-3 px-8 rounded-full text-sm font-semibold transition-all duration-200"
                        style={{ backgroundColor: '#5A7161', color: '#FFFFFF' }}
                        data-testid="offer-sur-mesure-cta"
                      >
                        Décrire mon besoin →
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mention bas de section */}
          <div className="mt-12 text-center max-w-2xl mx-auto">
            <p className="text-sm" style={{ color: '#5E6C60' }}>
              Toutes nos offres incluent l'installation, la personnalisation complète, la formation et le support technique.
            </p>
            <p className="text-sm font-medium mt-2" style={{ color: '#5A7161' }} data-testid="offres-guarantee-note">
              30 jours pour tester en conditions réelles — satisfait ou remboursé, sans justification.
            </p>
          </div>
        </div>
      </section>

      {/* ================================================================
          TABLEAU COMPARATIF
      ================================================================ */}
      <section className="py-20" style={{ backgroundColor: '#F4F0E8' }}>
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="label-tag mb-4 block" style={{ color: '#7D6235' }}>Détail</span>
            <h2 className="font-heading font-medium text-4xl sm:text-5xl" style={{ color: '#2C352D' }}>
              Comparatif complet
            </h2>
          </div>

          {/* Version tableau : tablette et desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse rounded-2xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(90,113,97,0.06)' }}>
                <thead>
                  <tr>
                    <th className="text-left py-4 px-5 text-sm font-medium" style={{ backgroundColor: '#F4F0E8', color: '#5E6C60', width: '40%' }}></th>
                    <th className="text-center py-4 px-4 text-sm font-semibold" style={{ backgroundColor: '#F4F0E8', color: '#2C352D' }}>Essentiel</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold" style={{ backgroundColor: 'rgba(90,113,97,0.08)', color: '#2C352D' }}>Pro</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold" style={{ backgroundColor: '#F4F0E8', color: '#2C352D' }}>Intégral</th>
                  </tr>
                </thead>
              <tbody>
                {comparisonRows.map((row, index) => (
                  <tr
                    key={index}
                    style={{
                      borderBottom: '1px solid #E2DFD8',
                      backgroundColor: index % 2 === 0 ? '#FFFFFF' : 'rgba(249,246,240,0.5)'
                    }}
                  >
                    <td className="py-3.5 px-5 text-sm font-medium" style={{ color: '#2C352D' }}>
                      {row.info ? (
                        <span className="inline-flex items-center gap-2">
                          {row.label}
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                aria-label="Détail de l'accompagnement à l'installation par offre"
                                className="inline-flex items-center justify-center rounded-full transition-opacity hover:opacity-70 focus:outline-none"
                                style={{ color: '#7D6235' }}
                                data-testid="install-support-info-trigger"
                              >
                                <Info className="h-4 w-4" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              collisionPadding={16}
                              className="w-[min(20rem,calc(100vw-2rem))] max-h-[65vh] overflow-y-auto rounded-2xl p-5"
                              style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2DFD8' }}
                              data-testid="install-support-popover"
                            >
                              <div className="space-y-4">
                                {mainOffers.filter(o => o.installSetup).map((offer) => (
                                  <div key={offer.id}>
                                    <p className="text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: '#7D6235' }}>
                                      {offer.name}
                                    </p>
                                    <ul className="space-y-1">
                                      {offer.installSetup.map((item, i) => (
                                        <li key={i} className="flex items-start gap-2 text-xs" style={{ color: '#5E6C60' }}>
                                          <span
                                            className="h-1 w-1 rounded-full flex-shrink-0 mt-1.5"
                                            style={{ backgroundColor: '#5A7161' }}
                                          />
                                          {item}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </span>
                      ) : row.label}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      {row.ess === true
                        ? <><svg aria-hidden="true" className="h-5 w-5 mx-auto" fill="none" stroke="#5A7161" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg><span className="sr-only">Inclus</span></>
                        : row.ess === false
                        ? <><svg aria-hidden="true" className="h-5 w-5 mx-auto" fill="none" stroke="#D1CFC9" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg><span className="sr-only">Non inclus</span></>
                        : <span className="text-xs" style={{ color: '#5E6C60' }}>{row.ess}</span>}
                    </td>
                    <td className="py-3.5 px-4 text-center" style={{ backgroundColor: 'rgba(90,113,97,0.05)' }}>
                      {row.pro === true
                        ? <><svg aria-hidden="true" className="h-5 w-5 mx-auto" fill="none" stroke="#5A7161" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg><span className="sr-only">Inclus</span></>
                        : row.pro === false
                        ? <><svg aria-hidden="true" className="h-5 w-5 mx-auto" fill="none" stroke="#D1CFC9" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg><span className="sr-only">Non inclus</span></>
                        : <span className="text-xs" style={{ color: '#5E6C60' }}>{row.pro}</span>}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      {row.prem === true
                        ? <><svg aria-hidden="true" className="h-5 w-5 mx-auto" fill="none" stroke="#5A7161" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg><span className="sr-only">Inclus</span></>
                        : row.prem === false
                        ? <><svg aria-hidden="true" className="h-5 w-5 mx-auto" fill="none" stroke="#D1CFC9" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg><span className="sr-only">Non inclus</span></>
                        : <span className="text-xs" style={{ color: '#5E6C60' }}>{row.prem}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Version accordéon : mobile uniquement */}
          <div className="md:hidden">
            <Accordion type="single" collapsible defaultValue={mainOffers[1]?.id} className="space-y-3">
              {mainOffers.map((offer, offerIndex) => {
                const planKey = planKeys[offerIndex];
                return (
                  <AccordionItem
                    key={offer.id}
                    value={offer.id}
                    className="rounded-2xl px-5"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2DFD8' }}
                    data-testid={`comparison-accordion-${offer.id}`}
                  >
                    <AccordionTrigger
                      className="text-left font-semibold py-4 hover:no-underline"
                      style={{ color: '#2C352D' }}
                    >
                      {offer.name}
                    </AccordionTrigger>
                    <AccordionContent className="pb-4">
                      <ul className="space-y-3">
                        {comparisonRows.map((row, index) => {
                          const value = row[planKey];
                          return (
                            <li key={index} className="flex items-start justify-between gap-3 text-sm" style={{ borderBottom: index < comparisonRows.length - 1 ? '1px solid #F0EDE5' : 'none', paddingBottom: '10px' }}>
                              <span style={{ color: '#2C352D' }}>{row.label}</span>
                              {value === true ? (
                                <>
                                  <svg aria-hidden="true" className="h-5 w-5 flex-shrink-0" fill="none" stroke="#5A7161" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                  <span className="sr-only">Inclus</span>
                                </>
                              ) : value === false ? (
                                <>
                                  <svg aria-hidden="true" className="h-5 w-5 flex-shrink-0" fill="none" stroke="#D1CFC9" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                  <span className="sr-only">Non inclus</span>
                                </>
                              ) : (
                                <span className="text-xs flex-shrink-0" style={{ color: '#5E6C60' }}>{value}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                      {offer.installSetup && (
                        <div className="mt-4 pt-4" style={{ borderTop: '1px solid #F0EDE5' }}>
                          <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#7D6235' }}>
                            Accompagnement à l'installation
                          </p>
                          <ul className="space-y-1.5">
                            {offer.installSetup.map((item, i) => (
                              <li key={i} className="flex items-start gap-2 text-xs" style={{ color: '#5E6C60' }}>
                                <span className="h-1 w-1 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: '#5A7161' }} />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </div>
      </section>


      {/* ================================================================
          FAQ
      ================================================================ */}
      <section className="py-20" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="label-tag mb-4 block" style={{ color: '#7D6235' }}>Questions</span>
            <h2 className="font-heading font-medium text-4xl sm:text-5xl" style={{ color: '#2C352D' }}>
              Questions fréquentes
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {faqsOffres.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="rounded-2xl px-6"
                style={{ backgroundColor: '#F4F0E8', border: '1px solid #E2DFD8' }}
              >
                <AccordionTrigger
                  className="text-left font-semibold py-5 hover:no-underline"
                  style={{ color: '#2C352D' }}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="pb-5 leading-relaxed" style={{ color: '#5E6C60' }}>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-10 text-center">
            <p className="text-sm mb-4" style={{ color: '#5E6C60' }}>
              Une question spécifique à votre pratique ?
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <button
                  className="py-3 px-8 rounded-full text-sm font-semibold"
                  style={{ backgroundColor: '#5A7161', color: '#FFFFFF' }}
                  data-testid="offres-faq-contact-cta"
                >
                  Posez-la nous directement →
                </button>
              </Link>
              <Link
                to="/blog"
                className="inline-flex items-center justify-center py-3 px-8 rounded-full text-sm font-semibold transition-colors"
                style={{ border: '2px solid #5A7161', color: '#5A7161' }}
                data-testid="offres-faq-blog-link"
              >
                Lire nos conseils sur le blog
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          CTA FINAL
      ================================================================ */}
      <section className="py-20 relative overflow-hidden" style={{ backgroundColor: '#2C352D' }}>
        <div className="absolute inset-0 opacity-30"
          style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(90,113,97,0.6) 0%, transparent 60%)' }} />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading font-medium text-4xl sm:text-5xl mb-6" style={{ color: '#F9F6F0' }}>
              Vous hésitez entre deux offres ?
            </h2>
            <p className="text-lg leading-relaxed mb-10" style={{ color: 'rgba(249,246,240,0.75)' }}>
              Échangeons 20 minutes. Vous nous décrivez votre pratique, nous vous conseillons la formule la plus adaptée — sans engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="rounded-full font-medium px-8"
                  style={{ backgroundColor: '#F9F6F0', color: '#2C352D' }}
                  data-testid="offres-final-cta"
                >
                  {globalCTA.primary}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/solution">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  style={{ borderColor: 'rgba(249,246,240,0.3)', color: '#F9F6F0' }}
                >
                  Voir la solution
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Offres;
